import { useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import Icon from '../Icon'
import { CHEERS, TAPS, correctText, pick, type AnswerValue, type Result, type Step } from '../../../shared/classroom'
import { L, lang, w } from '../../lib/words'
import { tapKick } from '../../lib/fun'
import Pichi, { type Mood } from './Pichi'

/** Lesson text: `**bold**` and line breaks, nothing else — and never as HTML. */
export function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n+/).map((para, i) => (
        <p key={i}>
          {para.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith('**') && part.endsWith('**') ? <strong key={j}>{part.slice(2, -2)}</strong> : part,
          )}
        </p>
      ))}
    </>
  )
}

const KIND_LABEL: Partial<Record<Step['type'], () => string>> = {
  discuss: () => w('discuss'),
  activity: () => w('activity'),
  poll: () => w('poll'),
}

/** The teaching part of a step: its title and body, or its prompt. */
export function StepBody({ step, big = false, teacher = false }: { step: Step; big?: boolean; teacher?: boolean }) {
  const label = KIND_LABEL[step.type]?.()
  return (
    <div className={`step step-${step.type}${big ? ' step-big' : ''}`}>
      {label && <div className="step-kind">{label}</div>}
      {(step.type === 'slide' || step.type === 'activity') && (
        <>
          <h2>{L(step.title)}</h2>
          <Rich text={L(step.body)} />
        </>
      )}
      {(step.type === 'question' || step.type === 'poll' || step.type === 'discuss') && <h2 className="prompt">{L(step.prompt)}</h2>}
      {teacher && step.teacherNote && (
        <div className="teacher-note"><strong>{w('teacherNote')}:</strong> <Rich text={L(step.teacherNote)} /></div>
      )}
    </div>
  )
}

const CHOICE_TONES = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5']

/**
 * The answer buttons. Every question gets "No sé" — saying so honestly is
 * rewarded, and it is what tells the teacher who to help.
 */
export function AnswerPad({ step, disabled, chosen, onAnswer }: {
  step: Step
  disabled?: boolean
  /** Live mode: the answer already sent, still changeable — its button stays marked. */
  chosen?: AnswerValue | null
  onAnswer: (a: AnswerValue) => void
}) {
  const [guess, setGuess] = useState(chosen && 'value' in chosen && typeof chosen.value === 'number' ? String(chosen.value) : '')
  const picked = (a: AnswerValue) => !!chosen && ('idk' in a ? 'idk' in chosen : 'value' in chosen && chosen.value === a.value)
  const mark = (a: AnswerValue) => (chosen ? (picked(a) ? ' picked' : ' unpicked') : '')
  const tick = (a: AnswerValue) => picked(a) && <span className="pick-tick"><Icon name="check" /></span>
  const fire = (a: AnswerValue, e: MouseEvent) => {
    if (disabled) return
    tapKick(e, L(pick(TAPS)))
    onAnswer(a)
  }
  let buttons: ReactNode = null
  if (step.type === 'question' && step.kind === 'yesno') {
    buttons = (
      <div className="pad pad-2">
        <button className={`choice c2${mark({ value: true })}`} aria-pressed={picked({ value: true })} disabled={disabled} onClick={(e) => fire({ value: true }, e)}>{w('yes')}{tick({ value: true })}</button>
        <button className={`choice c0${mark({ value: false })}`} aria-pressed={picked({ value: false })} disabled={disabled} onClick={(e) => fire({ value: false }, e)}>{w('no')}{tick({ value: false })}</button>
      </div>
    )
  } else if ((step.type === 'question' && step.kind === 'choice') || step.type === 'poll') {
    buttons = (
      <div className="pad">
        {step.choices.map((c, i) => (
          <button key={i} className={`choice ${CHOICE_TONES[i % CHOICE_TONES.length]}${mark({ value: i })}`} aria-pressed={picked({ value: i })} disabled={disabled} onClick={(e) => fire({ value: i }, e)}>
            <span className="choice-letter">{String.fromCharCode(65 + i)}</span>
            <span>{L(c)}</span>
            {tick({ value: i })}
          </button>
        ))}
      </div>
    )
  } else if (step.type === 'question' && step.kind === 'guess') {
    const n = Number(guess.replace(/[^0-9.-]/g, ''))
    buttons = (
      <form className="guess" onSubmit={(e) => e.preventDefault()}>
        <label>
          {w('yourGuess')}
          <div className="guess-row">
            {step.unit === '$' && <span className="guess-unit">$</span>}
            <input inputMode="decimal" value={guess} onChange={(e) => setGuess(e.target.value)} disabled={disabled} placeholder="0" />
            {step.unit && step.unit !== '$' && <span className="guess-unit">{step.unit}</span>}
          </div>
        </label>
        <button type="submit" className="choice c4" disabled={disabled || !guess.trim() || !Number.isFinite(n)} onClick={(e) => fire({ value: n }, e)}>
          {w('send')}
        </button>
      </form>
    )
  }
  return (
    <div className="answer">
      {buttons}
      {step.type === 'question' && (
        <button className={`choice idk${mark({ idk: true })}`} aria-pressed={picked({ idk: true })} disabled={disabled} onClick={(e) => fire({ idk: true }, e)}>
          <Icon name="question" /> {w('idk')}{tick({ idk: true })}
        </button>
      )}
    </div>
  )
}

const MOODS: Record<Result, Mood> = { right: 'cheer', close: 'wow', wrong: 'think', idk: 'happy', poll: 'wave' }

/** What a student sees after answering. Never negative — wrong is "almost", "No sé" is honest. */
export function Feedback({ step, result, xp, streak, onNext, after }: {
  step: Step; result: Result; xp: number; streak?: number
  /** Study mode: the big 'keep going' button. */
  onNext?: () => void
  /** Live mode: what happens next, e.g. 'eyes up front'. */
  after?: ReactNode
}) {
  const cheer = useMemo(() => L(pick(CHEERS[result])), [result])
  const answer = result !== 'right' && result !== 'poll' ? correctText(step, lang()) : null
  const explain = step.type === 'question' ? L(step.explain) : ''
  return (
    <div className={`feedback fb-${result}`} role="status">
      <Pichi size={92} stage={2} mood={MOODS[result]} />
      <div className="fb-head">
        <span className="fb-cheer">{cheer}</span>
        {xp > 0 && <span className="fb-xp"><Icon name="coin" size={18} /> +{xp}</span>}
      </div>
      {!!streak && streak >= 2 && result === 'right' && (
        <div className="fb-streak"><Icon name="flame" /> {streak} {w('streak')}</div>
      )}
      {answer && <p className="fb-answer">{w('answerWas')} <strong>{answer}</strong></p>}
      {explain && <div className="fb-explain"><Rich text={explain} /></div>}
      {after && <div className="fb-after">{after}</div>}
      {onNext && <button className="primary big fb-next" onClick={onNext}>{w('tapNext')} <Icon name="chevron" /></button>}
    </div>
  )
}

/** A level ring: how far to the next level, drawn as a circle. */
export function LevelRing({ progress, size = 96, children }: { progress: number; size?: number; children?: ReactNode }) {
  const r = size / 2 - 7
  const c = 2 * Math.PI * r
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} className="ring-track" />
        <circle cx={size / 2} cy={size / 2} r={r} className="ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - progress)} />
      </svg>
      <div className="ring-in">{children}</div>
    </div>
  )
}
